/**
 * Upload page for .eml file upload and parsing
 */
import React, { useState, useRef } from 'react';
import { CloudUpload, Trash2, Eye, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useVoucherStore } from '@/store/voucherStore';
import { formatCurrency, formatDate } from '@/utils/dateUtils';
import type { Voucher } from '@/types';
import { toast } from 'react-toastify';
import { cn } from '@/lib/utils';

export const Upload: React.FC = () => {
  const navigate = useNavigate();
  const { uploadEmlFile, isLoading } = useVoucherStore();
  const [file, setFile] = useState<File | null>(null);
  const [uploadedVouchers, setUploadedVouchers] = useState<Voucher[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.eml')) {
        setError('Please select a .eml file');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setUploadedVouchers([]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setError(null);
    try {
      const vouchers = await uploadEmlFile(file);
      setUploadedVouchers(vouchers);
      toast.success(`Successfully uploaded ${vouchers.length} voucher(s)`);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload file';
      setError(message);
      toast.error(message);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setUploadedVouchers([]);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.eml')) {
        setError('Please drop a .eml file');
        return;
      }
      setFile(droppedFile);
      setError(null);
      setUploadedVouchers([]);
    }
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-semibold">Upload Voucher</h1>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <h2 className="mb-2 text-lg font-semibold">Upload .eml File</h2>
          <p className="mb-6 text-sm text-muted-foreground">
            Upload an email file (.eml) from Gyftr or iShop to automatically extract voucher
            information.
          </p>

          <div
            role="button"
            tabIndex={0}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition-colors hover:border-primary hover:bg-accent/50'
            )}
          >
            <CloudUpload className="mb-4 size-12 text-muted-foreground" />
            <p className="mb-1 font-medium">
              {file ? file.name : 'Click to select or drag and drop .eml file here'}
            </p>
            <p className="text-sm text-muted-foreground">Supported formats: .eml</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".eml"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {file && (
            <div className="mt-6">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm">{file.name}</p>
                <Button variant="ghost" size="icon-sm" onClick={handleRemoveFile} aria-label="Remove file">
                  <Trash2 className="size-4" />
                </Button>
              </div>
              <Button
                className="w-full"
                onClick={handleUpload}
                disabled={isLoading}
              >
                {isLoading ? 'Uploading...' : 'Upload and Parse'}
              </Button>
              {isLoading && <Progress value={undefined} className="mt-2" />}
            </div>
          )}

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {uploadedVouchers.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Uploaded Vouchers ({uploadedVouchers.length})
              </h2>
              <Button variant="outline" onClick={() => navigate('/vouchers')}>
                View All Vouchers
              </Button>
            </div>
            <ul className="space-y-2">
              {uploadedVouchers.map((voucher) => (
                <li
                  key={voucher.id}
                  className="flex items-center justify-between gap-4 rounded-lg border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{voucher.brand_name}</span>
                      <CheckCircle className="size-4 shrink-0 text-success" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(voucher.value)} • {voucher.source_platform}
                    </p>
                    {voucher.expiry_date && (
                      <p className="text-xs text-muted-foreground">
                        Expires: {formatDate(voucher.expiry_date)}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => navigate(`/vouchers/${voucher.id}`)}
                    aria-label="View details"
                  >
                    <Eye className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
